import { isEmpty } from 'smoothie';

import type { Cents, Dollars, FormattedDollars } from '../types/Currency';

const ONLY_DIGITS_REGEX = /^\d+$/;
const NON_DIGIT_REGEX = /\D/;
const NOT_CURRENCY_CHARACTERS_REGEX = /[^-?\d+(\\.\d{2})?]/g;
const THOUSANDS_PLACE_COMMAS_REGEX = /\B(?=(\d{3})+(?!\d))/g;
const NO_LEADING_ZEROS_REGEX = /^0+(?!$)/;
const FORMATTED_CURRENCY_REGEX =
    /^(\$?[0-9])(\d{0,2}(?:,\d{3})*(?:\.\d{0,2})?)$/;
const DOLLAR_SIGN_WITH_SPACE = '$ ';

export const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
};

export const centsToDollars = (cents: Cents): Dollars => {
    return roundToTwoDecimals(cents / 100);
};

export const formattedDollarsToCents = (
    formattedDollars: FormattedDollars,
): Cents => {
    // Remove anything other than digits and decimal points (i.e. strip out any currency formatting)
    const unformattedDollars = formattedDollars.replace(
        NOT_CURRENCY_CHARACTERS_REGEX,
        '',
    );

    // Round to handle any floating point precision issues
    return Math.round(parseFloat(unformattedDollars) * 100);
};

// Custom formatter for currency displays in member profile.
export const centsToFormattedDollars = (cents?: Cents | null) => {
    if (cents == null) {
        return DOLLAR_SIGN_WITH_SPACE;
    }

    const dollars = centsToDollars(cents);
    const dollarsAsString = dollars.toString();

    if (isEmpty(dollarsAsString)) {
        return DOLLAR_SIGN_WITH_SPACE;
    }

    // Add in commas every thousand's place
    const formattedValue = dollarsAsString.replace(
        THOUSANDS_PLACE_COMMAS_REGEX,
        ',',
    );

    // If we don't have a valid formatted number, return the previous value
    if (!FORMATTED_CURRENCY_REGEX.exec(formattedValue)) {
        return DOLLAR_SIGN_WITH_SPACE;
    } else {
        return `${DOLLAR_SIGN_WITH_SPACE}${formattedValue}`;
    }
};

// Custom formatter for currency displays in member profile.
export const formatCurrencyInputOnChange = (
    previousValue: string,
    nextValue: string,
) => {
    // Remove anything other than digits and decimal points (i.e. strip out any currency formatting)
    nextValue = nextValue.replace(NOT_CURRENCY_CHARACTERS_REGEX, '');

    if (isEmpty(nextValue)) {
        return DOLLAR_SIGN_WITH_SPACE;
    }

    // Remove leading zeros unless the value is just '0' (e.g. '00012' becomes '12', but '0000' becomes '0')
    nextValue = nextValue.replace(NO_LEADING_ZEROS_REGEX, '');

    // Add in commas every thousand's place
    const formattedValue = nextValue.replace(THOUSANDS_PLACE_COMMAS_REGEX, ',');

    // If we don't have a valid formatted number, return the previous value or default
    if (!FORMATTED_CURRENCY_REGEX.exec(formattedValue)) {
        return previousValue ?? DOLLAR_SIGN_WITH_SPACE;
    } else {
        return `${DOLLAR_SIGN_WITH_SPACE}${formattedValue}`;
    }
};

// Custom formatter for numeric displays in member profile.
export const formatNumericInputOnChange = (
    previousValue: string,
    nextValue: string,
) => {
    // Remove anything other than digits.
    nextValue = nextValue.replace(NON_DIGIT_REGEX, '');

    if (isEmpty(nextValue)) {
        return '';
    }

    // Remove leading zeros unless the value is just '0' (e.g. '00012' becomes '12', but '0000' becomes '0')
    const formattedValue = nextValue.replace(NO_LEADING_ZEROS_REGEX, '');

    // If we don't have a valid formatted number, return the previous value or default
    if (!ONLY_DIGITS_REGEX.exec(formattedValue)) {
        return previousValue ?? '';
    } else {
        return formattedValue;
    }
};
